import { useOrderTask } from '@/modules/Tasks/queries';
import { useOrderWorkflow } from '@/modules/Workflows/queries';
import { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useEffect, useState } from 'react';
import { ProcessDetailsWorkflow, ProcessDetailsWorkflowTask } from '../domain';

const handleMoveTasks = (
  event: DragEndEvent,
  workflows: ProcessDetailsWorkflow[],
) => {
  const activeTask = event.active.data.current as ProcessDetailsWorkflowTask;
  const currentWorkflow = workflows.find((workflow) =>
    workflow.tasks.some((task) => task.id === activeTask.id),
  );

  const overObject = event.over?.data.current;

  if (!currentWorkflow) {
    return { workflows };
  }

  if (!event.over || !overObject) {
    const overIndex = currentWorkflow.tasks.findIndex(
      (task) => task.id === activeTask.id,
    );
    return { workflows, activeTask, overIndex, overWorkflow: currentWorkflow };
  }

  if (event.over.id.toString().startsWith('w_')) {
    const overWorkflow = overObject as ProcessDetailsWorkflow;
    return { workflows, activeTask, overIndex: 0, overWorkflow };
  }

  const overTask = overObject as ProcessDetailsWorkflowTask;

  const overWorkflow = workflows.find((workflow) =>
    workflow.tasks.some((task) => task.id === overTask.id),
  );

  const overIndex =
    overWorkflow?.tasks.map((task) => task.id).indexOf(overTask.id) ?? 0;

  if (
    overIndex < 0 ||
    !overWorkflow ||
    currentWorkflow.id !== overWorkflow.id
  ) {
    return { workflows };
  }

  const tasks = overWorkflow.tasks.map((task) => task.id);

  overWorkflow.tasks = arrayMove(
    overWorkflow.tasks,
    tasks.indexOf(activeTask.id),
    overIndex,
  );

  return { workflows: [...workflows], activeTask, overIndex, overWorkflow };
};

const handleMoveOverTasksBetweenWorkflows = (
  event: DragOverEvent,
  workflows: ProcessDetailsWorkflow[],
) => {
  const activeTask = event.active.data.current as ProcessDetailsWorkflowTask;
  const currentWorkflow = workflows.find((workflow) =>
    workflow.tasks.some((task) => task.id === activeTask.id),
  );

  const overObject = event.over?.data.current;

  if (!currentWorkflow || !event.over || !overObject) {
    return workflows;
  }

  let overWorkflow: ProcessDetailsWorkflow | undefined;

  if (event.over.id.toString().startsWith('w_')) {
    overWorkflow = overObject as ProcessDetailsWorkflow;
  } else {
    const overTask = overObject as ProcessDetailsWorkflowTask;
    overWorkflow = workflows.find((workflow) =>
      workflow.tasks.some((task) => task.id === overTask.id),
    );
  }

  if (!overWorkflow || overWorkflow.id === currentWorkflow.id) {
    return workflows;
  }

  currentWorkflow.tasks = currentWorkflow.tasks.filter(
    (task) => task.id !== activeTask.id,
  );

  overWorkflow.tasks.push(activeTask);

  return [...workflows];
};

const getActiveWorkflow = (
  event: DragEndEvent,
  workflows: ProcessDetailsWorkflow[],
) => {
  const activeWorkflow = event.active.data.current as ProcessDetailsWorkflow;
  const activeIndex = workflows.findIndex((w) => w.id === activeWorkflow.id);
  return { id: activeWorkflow.id, activeIndex };
};

type useWorkflowsDndProps = {
  initialWorkflows: ProcessDetailsWorkflow[];
};

export const useWorkflowsDragAndDrop = ({
  initialWorkflows = [],
}: useWorkflowsDndProps) => {
  const [workflows, setWorkflows] = useState(initialWorkflows);
  useEffect(() => setWorkflows(initialWorkflows), [initialWorkflows]);
  const { mutate: orderTask, isLoading: isSubmittingOrderTasks } =
    useOrderTask();
  const { mutate: orderWorkflow, isLoading: isSubmittingOrderWorkflows } =
    useOrderWorkflow();

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.active.id.toString().startsWith('w_')) {
        const activeWorkflow = getActiveWorkflow(event, workflows);

        if (!activeWorkflow) {
          return;
        }

        orderWorkflow({
          id: activeWorkflow.id,
          order: activeWorkflow.activeIndex + 1,
        });

        return;
      }

      const {
        workflows: newWorkflows,
        activeTask,
        overIndex,
        overWorkflow,
      } = handleMoveTasks(event, workflows);

      if (!activeTask || overIndex === undefined || !overWorkflow) {
        return;
      }

      setWorkflows(newWorkflows);
      orderTask({
        id: activeTask.id,
        order: overIndex + 1,
        workflowId: overWorkflow.id,
      });
    },
    [orderTask, orderWorkflow, workflows],
  );

  const onDragOver = useCallback(
    (event: DragOverEvent) => {
      if (event.active.id.toString().startsWith('w_')) {
        const activeWorkflow = getActiveWorkflow(event, workflows);

        if (!activeWorkflow) {
          return;
        }

        const overWorkflow = event.over?.data.current as ProcessDetailsWorkflow;

        if (!overWorkflow) {
          return;
        }

        const overIndex = workflows.findIndex((w) => w.id === overWorkflow.id);

        setWorkflows(
          arrayMove(workflows, activeWorkflow.activeIndex, overIndex),
        );
        return;
      }

      setWorkflows(handleMoveOverTasksBetweenWorkflows(event, workflows));
    },
    [workflows],
  );

  return {
    workflows,
    onDragEnd,
    onDragOver,
    isSubmittingOrderTasks,
    isSubmittingOrderWorkflows,
  };
};
